import React from "react";
import CustomModal from "components/custom-modal/CustomModal";
import Button from "components/button/Button";
import CardNumber from "components/card-number/CardNumber";
import SearchableDropdown from "components/searchable-dropdown/SearchableDropdown";
import type { DropdownOption } from "components/searchable-dropdown/types";
import { XIconV1 } from "images/image-component";
import Spinner from "images/image-component/Spinner";
import LoadingModal from "modals/loading/LoadingModal";
import ErrorModal from "modals/error-modal/ErrorModal";
import SuccessModal from "modals/success-modal/SuccessModal";
import {
  useTokenApprovals,
  tokenRefLabel,
  ApprovalStep,
  ApprovalSubject,
} from "./useTokenApprovals";
import type { ApprovalTokenRef } from "types/approvals";
import "./approvals-modal.css";

export interface ApprovalsModalProps {
  isOpen: boolean;
  onClose: () => void;
  kind: ApprovalSubject;
  /** cardNumber or merchantId — what the API keys on. */
  id: string;
  /** Merchant name. Cards are identified by their number, so they pass nothing. */
  displayName?: string;
}

const TOTAL_STEPS = 2;

const STEP_COPY: Record<ApprovalStep, { title: string; subtitle: string }> = {
  1: {
    title: "Add Currency",
    subtitle: "Check which supported currencies are missing an approval.",
  },
  2: {
    title: "Grant Approval",
    subtitle: "Review the details, then grant the allowance on-chain.",
  },
};

function InfoRow({
  label,
  children,
  stack,
}: {
  label: string;
  children: React.ReactNode;
  stack?: boolean;
}) {
  return (
    <div className={`ap-row${stack ? " ap-row--stack" : ""}`}>
      <span className="ap-row__label">{label}</span>
      <span className="ap-row__val">{children}</span>
    </div>
  );
}

function ApprovalsModal({
  isOpen,
  onClose,
  kind,
  id,
  displayName,
}: ApprovalsModalProps) {
  const {
    step,
    outcome,
    noticeMsg,
    isChecking,
    recheck,
    walletAddress,
    subjectStatus,
    spender,
    approved,
    missing,
    selectedToken,
    selectToken,
    goToGrant,
    goBack,
    isGranting,
    grantErrorMsg,
    grantSuccessMsg,
    grant,
    dismissGrantError,
    acknowledgeGrant,
  } = useTokenApprovals(kind, id, isOpen);

  const subjectNoun = kind === "card" ? "Card" : "Merchant";
  const { title, subtitle } = STEP_COPY[step];
  const selectedLabel = tokenRefLabel(selectedToken);

  const tokenOptions: DropdownOption<string>[] = missing.map(
    (token: ApprovalTokenRef) => ({
      value: token.address,
      label: tokenRefLabel(token),
      keywords: token.address,
    }),
  );

  const noticeTone =
    outcome === "failed"
      ? " ap-notice--bad"
      : outcome === "unavailable"
        ? " ap-notice--warn"
        : " ap-notice--ok";

  return (
    <>
      <CustomModal isOpen={isOpen}>
        <button
          className="v1-modal-close"
          aria-label="Close"
          onClick={onClose}
          disabled={isGranting}
        >
          <XIconV1 />
        </button>

        <div className="ap-header">
          {/* <span className="ap-step">
            Step {step} of {TOTAL_STEPS}
          </span> */}
          <h2 className="v1-modal-title">{title}</h2>
          <p className="v1-modal-subtitle">{subtitle}</p>
        </div>

        <div className="ap-rows">
          {kind === "card" ? (
            <InfoRow label="Card">
              <CardNumber value={id} />
            </InfoRow>
          ) : (
            <>
              <InfoRow label="Merchant">{displayName || id}</InfoRow>
              {/* <InfoRow label="Merchant ID">
                <span className="ap-mono">{id}</span>
              </InfoRow> */}
            </>
          )}

          {/* <InfoRow label="Wallet" stack>
            <span className="ap-mono">{walletAddress || "—"}</span>
          </InfoRow> */}

          {/* {subjectStatus && (
            <InfoRow label={`${subjectNoun} status`}>{subjectStatus}</InfoRow>
          )} */}

          {/* Only on step 1: step 2 names the one currency being granted, and repeating the
              full picture there just makes the review longer to read. */}
          {step === 1 && (
            <>
              <InfoRow label="Supported">
                {approved.length > 0
                  ? approved.map(tokenRefLabel).join(", ")
                  : "None"}
              </InfoRow>
{/* 
              <InfoRow label="Missing">
                {missing.length > 0
                  ? missing.map(tokenRefLabel).join(", ")
                  : "None"}
              </InfoRow> */}
            </>
          )}

          {step === 2 && (
            <>
              <InfoRow label="Currency" stack>
                <span className="ap-row__val-strong">{selectedLabel}</span>
                <span className="ap-mono">{selectedToken?.address}</span>
              </InfoRow>
              <InfoRow label="Spender" stack>
                <span className="ap-mono">{spender || "—"}</span>
              </InfoRow>
            </>
          )}
        </div>

        {step === 1 && (
          <>
            {outcome === "checking" ? (
              <div className="ap-status">
                <Spinner size={16} color="var(--brand-hi)" />
                Checking on-chain approvals…
              </div>
            ) : outcome === "missing" ? (
              <div className="ap-field">
                <SearchableDropdown<string>
                  name="ap-token"
                  label="Currency to approve"
                  value={selectedToken?.address ?? null}
                  onChange={selectToken}
                  options={tokenOptions}
                  placeholder="Select a currency"
                  searchPlaceholder="Search currencies…"
                  disabled={isChecking}
                />
                <p className="ap-hint">
                  One currency per grant. Repeat the flow for the rest.
                </p>
              </div>
            ) : (
              <p className={`ap-notice${noticeTone}`}>{noticeMsg}</p>
            )}

            {isChecking && outcome !== "checking" && (
              <p className="ap-hint">Re-checking…</p>
            )}
          </>
        )}

        {step === 2 && (
          <p className="ap-notice ap-notice--warn">
            This sends on-chain transactions to approve {selectedLabel} on
            {kind}.
          </p>
        )}

        <div className="v1-modal-actions">
          {step === 1 ? (
            <>
              <Button variant="ghost" onClick={onClose}>
                Close
              </Button>
              <Button
                variant="subtle"
                onClick={recheck}
                disabled={isChecking}
              >
                Re-check
              </Button>
              {/* {outcome === "missing" && (
                <Button
                  onClick={goToGrant}
                  disabled={!selectedToken || isChecking}
                >
                  Continue
                </Button>
              )} */}
              {outcome === "missing" && (
                <Button
                  onClick={grant}
                  disabled={!selectedToken || isChecking}
                >
                  Grant approval
                </Button>
              )}
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={goBack} disabled={isGranting}>
                Back
              </Button>
              <Button onClick={grant} disabled={isGranting || !selectedToken}>
                Grant approval
              </Button>
            </>
          )}
        </div>
      </CustomModal>

      <LoadingModal
        isOpen={isGranting}
        loadingMsg={`Granting the ${selectedLabel} approval… this sends two transactions, so it can take a moment.`}
      />

      <ErrorModal
        isOpen={!!grantErrorMsg}
        errorMsg={grantErrorMsg}
        handleClose={dismissGrantError}
        tryAgain={grant}
      />

      <SuccessModal
        isOpen={!!grantSuccessMsg}
        message={grantSuccessMsg}
        handleClose={acknowledgeGrant}
      />
    </>
  );
}

export default ApprovalsModal;
